// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useMemo, useState } from 'react';
import { useI18n } from '../i18n';

/**
 * Tableau générique triable (clic sur en-tête) + filtrable (recherche texte libre).
 *
 * columns: [{ key, label, sortable=true, align='left', render?(row) }]
 * rows: array d'objets (doivent contenir les clés référencées par `columns`)
 * searchKeys: clés sur lesquelles porte la recherche texte libre (défaut: toutes les colonnes)
 * searchPlaceholder: placeholder du champ de recherche
 * emptyLabel: texte affiché si `rows` est vide
 * hideSearch: masque le champ de recherche texte libre intégré (quand un filtre
 *   dropdown externe pré-filtre déjà `rows` avant de les passer à ce composant)
 * onSortChange: notifié à chaque changement de tri avec `{key, dir}` ou `null`
 *   (3e clic = tri annulé). Optionnel — sert à l'appelant qui doit RÉAGIR au
 *   tri, par exemple l'onglet Organisation du portail licence : il affiche un
 *   arbre indenté tant qu'aucune colonne n'est triée, et bascule à plat dès
 *   qu'il y en a une, l'indentation mentant sur l'ordre une fois trié.
 */
const SortableFilterableTable = ({
  columns,
  rows,
  searchKeys,
  searchPlaceholder = 'Rechercher…',
  emptyLabel = 'Aucune donnée.',
  hideSearch = false,
  onSortChange,
}) => {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(null); // { key, dir: 'asc' | 'desc' }

  const keys = searchKeys || columns.map(c => c.key);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter(r => keys.some(k => String(r[k] ?? '').toLowerCase().includes(q)));
  }, [rows, search, keys]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const { key, dir } = sort;
    const copy = [...filtered];
    copy.sort((a, b) => {
      const va = a[key], vb = b[key];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return dir === 'asc' ? va - vb : vb - va;
      return dir === 'asc'
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
    return copy;
  }, [filtered, sort]);

  // L'état suivant est calculé HORS de l'updater : notifier l'appelant depuis
  // l'intérieur d'un `setState` fonctionnel produirait un effet de bord pendant
  // le rendu, double-invoqué en StrictMode.
  const toggleSort = (key) => {
    const suivant = (!sort || sort.key !== key)
      ? { key, dir: 'asc' }
      : sort.dir === 'asc' ? { key, dir: 'desc' } : null;
    setSort(suivant);
    if (onSortChange) onSortChange(suivant);
  };

  // Affordance PERMANENTE : la colonne triable porte un ⇅ estompé même quand
  // aucun tri n'est actif. Sans lui, rien à l'écran ne disait que les en-têtes
  // étaient cliquables — il fallait cliquer au hasard pour le découvrir.
  const indicateurTri = (col) => {
    if (col.sortable === false) return null;
    const actif = sort?.key === col.key;
    return (
      <span aria-hidden="true" style={{ marginLeft: 4, opacity: actif ? 1 : 0.3, fontSize: 11 }}>
        {actif ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}
      </span>
    );
  };

  return (
    <div>
      {!hideSearch && (
        <div style={{ marginBottom: 8 }}>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12, width: 240 }}
          />
        </div>
      )}
      <div className="admin-table-wrapper" style={{ background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--white)', borderBottom: '1px solid var(--gray)' }}>
              {columns.map(col => (
                <th
                  key={col.key}
                  onClick={col.sortable === false ? undefined : () => toggleSort(col.key)}
                  title={col.sortable === false ? undefined : t('table_sort_hint')}
                  style={{
                    padding: '10px 14px',
                    textAlign: col.align || 'left',
                    fontWeight: 600,
                    color: 'var(--black)',
                    whiteSpace: 'nowrap',
                    cursor: col.sortable === false ? 'default' : 'pointer',
                    userSelect: 'none',
                  }}
                >
                  {col.label}
                  {indicateurTri(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)' }}>
                  {emptyLabel}
                </td>
              </tr>
            ) : sorted.map((row, i) => (
              <tr key={row.id ?? i} style={{ borderBottom: '1px solid var(--gray)', background: i % 2 === 0 ? 'transparent' : 'rgba(128,128,128,0.03)' }}>
                {columns.map(col => (
                  <td key={col.key} style={{ padding: '10px 14px', textAlign: col.align || 'left', color: 'var(--black)' }}>
                    {col.render ? col.render(row) : (row[col.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SortableFilterableTable;
